<template>
  <div class="tm-person">
    <div class="tm-person__avatar">
      <slot name="avatar">
        <tm-avatar
          v-bind="placeholderProps"
          :url="avatarUrl"
          :name="avatarName || name"
          :color="avatarColor"
          :size="avatarSize"
          :placeholder-custom-size="avatarPlaceholderCustomSize"
          :custom-size="avatarCustomSize"
          :exact-color="exactColor"
          :override-text-color="overrideTextColor"
        />
      </slot>
    </div>
    <span
      v-if="name || $slots['name']"
      class="tm-person__name"
      :class="{
        'font-weight-semi-bold': semiBold,
        'tm-person__name--hoverable': hoverable,
      }"
      :style="{ 'max-width': maxWidth }"
    >
      <slot
        name="name"
        v-bind="{ name }"
      >
        <person-name-highlight
          :name="name"
          :search="searchQuery"
        />
      </slot>
    </span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from '@/composition/vue/compositionApi'

import type { StylesheetSizeProp } from '@/types'
import type { IconName } from '@/assets/icons/icons'

import type { AvatarSize, SizeProp } from '@/components/shared/types'
import TmAvatar from '@/components/shared/TmAvatar.vue'
import PersonNameHighlight from '@/components/shared/misc/PersonNameHighlight.vue'

import type { AllAvatarColors } from '@/constants/avatar'
import type { StyleColors } from '@/constants/styleColors'

export default defineComponent({
  components: {
    PersonNameHighlight,
    TmAvatar,
  },
  props: {
    name: {
      type: String,
    },
    avatarName: {
      type: String,
    },
    avatarUrl: {
      type: String,
    },
    avatarColor: {
      type: String as PropType<AllAvatarColors>,
    },
    avatarIcon: {
      type: String as PropType<IconName>,
    },
    avatarSize: {
      type: String as SizeProp<AvatarSize>,
      default: 'medium',
    },
    avatarCustomSize: {
      type: String,
    },
    avatarPlaceholderCustomSize: {
      type: String,
    },
    semiBold: {
      type: Boolean,
    },
    searchQuery: {
      type: String,
    },
    maxWidth: {
      type: String as PropType<StylesheetSizeProp>,
    },
    hoverable: {
      type: Boolean,
      default: false,
    },
    exactColor: {
      type: Boolean,
    },
    overrideTextColor: {
      type: String as PropType<StyleColors>,
    },
  },
  setup(props) {
    const placeholderProps = computed(() => {
      const obj: Record<string, any> = {}
      if (props.avatarIcon) {
        obj.placeholderType = 'icon'
        obj.customIcon = props.avatarIcon
      }
      return obj
    })

    return {
      placeholderProps,
    }
  },
})
</script>

<style lang="scss" scoped>
.tm-person {
  display: flex;
  align-items: center;
  overflow: hidden;
  gap: 8px;
  font-weight: inherit;

  &__name {
    line-height: 20px;
    font-weight: inherit;
    color: var(--tm-person-base-color, $emphasize);
    overflow: hidden;
    text-overflow: ellipsis;

    &--hoverable:hover {
      color: var(--tm-person-base-color-hover, $blue);
    }
  }
}
</style>
